import axios from 'axios';
import { getAiUrl, getAppOrigin } from '../utils/common';
import type { Content } from './StorageService';

export interface GenerateImagePayload
{
    prompt: string;
    n?: number;
    size?: '1024x1024' | '1024x1536' | '1536x1024';
    quality?: 'low' | 'medium' | 'high';
}

export interface GeneratedImage
{
    base64: string;
    mimeType: string;
}

export interface GenerateImageResponse
{
    images: GeneratedImage[];
}

function base64ToFile(base64: string, mimeType: string, filename: string)
{
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));

    return new File([bytes], filename, { type: mimeType });
}

function mimeToExtension(mimeType: string)
{
    const map: Record<string, string> = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/webp': 'webp',
        'image/gif': 'gif'
    };

    return map[mimeType] ?? 'png';
}

export default class ImageGeneratorService
{
    private static getHeaders(includeSessionId: boolean = true)
    {
        const { $auth } = useAuth();

        const headers: Record<string, string> = {
            Authorization: `Bearer ${$auth.token()}`
        };

        if (includeSessionId)
        {
            if (!sessionStorage.getItem('ai-session-id'))
            {
                sessionStorage.setItem('ai-session-id', crypto.randomUUID());
            }

            headers['X-Session-Id'] = sessionStorage.getItem('ai-session-id')!;
        }

        return headers;
    }

    public static async generate(payload: GenerateImagePayload)
    {
        return (await axios.post<GenerateImageResponse>(`${getAiUrl()}/ai/admin/image/create`, payload, { headers: this.getHeaders() })).data;
    }

    public static async removeBackground(imageUrl: string)
    {
        const imagePath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;

        return (await axios.post<GeneratedImage>(
            `${getAiUrl()}/ai/admin/image/remove-background`,
            { imageUrl: imagePath },
            { headers: this.getHeaders() }
        )).data;
    }

    public static async generateMetadata(imageUrl: string)
    {
        const fullImageUrl = `${getAppOrigin()}/${imageUrl.replace(/^\//, '')}`;
        const imageResponse = await axios.get<Blob>(fullImageUrl, {
            headers: this.getHeaders(false),
            responseType: 'blob'
        });
        const imageBlob = imageResponse.data;

        const imageBase64 = await new Promise<string>((resolve, reject) =>
        {
            const reader = new FileReader();

            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
        });

        return (await axios.post<Record<string, Content>>(
            `${getAiUrl()}/ai/admin/vision/metadata`,
            { image: imageBase64 },
            { headers: this.getHeaders() }
        )).data;
    }

    public static async saveImages(
        images: GeneratedImage[],
        scope: string,
        folder?: number,
        forceConvert: boolean = true
    )
    {
        const results: FileResource[] = [];

        for (let i = 0; i < images.length; i++)
        {
            const img = images[i];
            const ext = mimeToExtension(img.mimeType);
            const filename = `ai-generated-${Date.now()}-${i}.${ext}`;
            const file = base64ToFile(img.base64, img.mimeType, filename);

            const data = new FormData();

            data.append('file', file, filename);
            data.append('container', 'default');
            data.append('scope', scope);
            data.append('forceConvert', forceConvert ? 'true' : 'false');

            if (folder)
            {
                data.append('folder', folder.toString());
            }

            const result = await axios.post<FileResource>('admin/storage/upload', data);

            results.push(result.data);
        }

        return results;
    }
}
